import { apiClient } from './apiClient';
import type { TimesheetReportEntry } from '@/types/timesheetReport';

export interface PagedTimesheetReportResponse {
  items: TimesheetReportEntry[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  summary?: ReportSummaryDto;
}

export interface ReportSummaryDto {
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  billablePercentage: number;
  uniqueEmployees: number;
  uniqueProjects: number;
  entriesWithRejections: number;
  totalEntries: number;
}

export interface TimesheetReportQueryParams {
  dateFrom?: Date;
  dateTo?: Date;
  employeeIds?: number[];
  projectIds?: string[];
  statuses?: string[];
  departments?: string[];
  billableOnly?: boolean;
  rejectedOnly?: boolean;
  overtimeOnly?: boolean;
  overtimeTypeIds?: string[];
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: string;
  includeSummary?: boolean;
  companyId?: string;
}

/**
 * Build query string from parameters
 * Arrays are repeated as multiple query parameters (e.g., employeeIds=E1&employeeIds=E2)
 */
function buildQueryString(params: TimesheetReportQueryParams): string {
  const queryParts: string[] = [];

  if (params.dateFrom) {
    queryParts.push(`dateFrom=${params.dateFrom.toISOString().split('T')[0]}`);
  }
  if (params.dateTo) {
    queryParts.push(`dateTo=${params.dateTo.toISOString().split('T')[0]}`);
  }

  if (params.employeeIds && params.employeeIds.length > 0) {
    params.employeeIds.forEach(id => {
      queryParts.push(`employeeIds=${encodeURIComponent(id)}`);
    });
  }
  if (params.projectIds && params.projectIds.length > 0) {
    params.projectIds.forEach(id => {
      queryParts.push(`projectIds=${encodeURIComponent(id)}`);
    });
  }
  if (params.statuses && params.statuses.length > 0) {
    params.statuses.forEach(status => {
      queryParts.push(`statuses=${encodeURIComponent(status)}`);
    });
  }
  if (params.departments && params.departments.length > 0) {
    params.departments.forEach(dept => {
      queryParts.push(`departments=${encodeURIComponent(dept)}`);
    });
  }

  if (params.billableOnly !== undefined) {
    queryParts.push(`billableOnly=${params.billableOnly}`);
  }
  if (params.rejectedOnly !== undefined) {
    queryParts.push(`rejectedOnly=${params.rejectedOnly}`);
  }

  if (params.overtimeOnly !== undefined) {
    queryParts.push(`overtimeOnly=${params.overtimeOnly}`);
  }
  if (params.overtimeTypeIds && params.overtimeTypeIds.length > 0) {
    params.overtimeTypeIds.forEach(id => {
      queryParts.push(`overtimeTypeIds=${encodeURIComponent(id)}`);
    });
  }

  if (params.pageNumber !== undefined) {
    queryParts.push(`pageNumber=${params.pageNumber}`);
  }
  if (params.pageSize !== undefined) {
    queryParts.push(`pageSize=${params.pageSize}`);
  }

  if (params.sortBy) {
    queryParts.push(`sortBy=${encodeURIComponent(params.sortBy)}`);
  }
  if (params.sortDir) {
    queryParts.push(`sortDir=${encodeURIComponent(params.sortDir)}`);
  }

  if (params.includeSummary !== undefined) {
    queryParts.push(`includeSummary=${params.includeSummary}`);
  }

  if (params.companyId) {
    queryParts.push(`companyId=${encodeURIComponent(params.companyId)}`);
  }

  return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
}

/**
 * Parse date strings in API response to Date objects
 */
function parseReportEntryDates(entry: any): TimesheetReportEntry {
  return {
    ...entry,
    weekEnding: entry.weekEnding ? new Date(entry.weekEnding) : entry.weekEnding,
    submittedAt: entry.submittedAt ? new Date(entry.submittedAt) : entry.submittedAt,
    approvedAt: entry.approvedAt ? new Date(entry.approvedAt) : entry.approvedAt,
    rejectedAt: entry.rejectedAt ? new Date(entry.rejectedAt) : entry.rejectedAt,
  };
}

/**
 * Reports API service for timesheet reporting endpoints
 */
export const reportsApi = {
  /**
   * Get timesheet report with filtering, pagination, and sorting
   */
  async getTimesheetReport(params: TimesheetReportQueryParams = {}): Promise<PagedTimesheetReportResponse> {
    const queryString = buildQueryString(params);
    const response = await apiClient.get<any>(`/reports/timesheets${queryString}`);
    
    return {
      ...response,
      items: response.items.map(parseReportEntryDates),
    };
  },

  /**
   * Get timesheet report summary only (aggregated statistics)
   */
  async getTimesheetReportSummary(params: Omit<TimesheetReportQueryParams, 'pageNumber' | 'pageSize' | 'sortBy' | 'sortDir' | 'includeSummary'>): Promise<ReportSummaryDto> {
    const queryString = buildQueryString(params);
    return apiClient.get<ReportSummaryDto>(`/reports/timesheets/summary${queryString}`);
  },
};
